import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Info, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CTABanner } from "../components/CTABanner";
import { SEO } from "../components/SEO";
import { INSURERS } from "../data/siteData";

const spring = { type: "spring", stiffness: 200, damping: 20 };

const INSURANCE_STEPS = [
  {
    step: "01",
    title: "Get a Referral",
    text: "Obtain a medical referral from your child's pediatrician.",
  },
  {
    step: "02",
    title: "Send Us Your Details",
    text: "Provide Thrive with the referral and your child's insurance information.",
  },
  {
    step: "03",
    title: "We Verify Your Benefits",
    text: "Our team will verify your child's available therapy benefits and let you know what to expect.",
  },
];

export default function Insurance() {
  return (
    <div data-testid="insurance-page">
      <SEO
        title="Using Insurance | Thrive Pediatric Therapy, Nassau"
        description="Thrive accepts all major health insurance providers in The Bahamas, including Colina, CG Atlantic, BahamaHealth and BAF. Learn about referrals, benefits verification and our simple three-step process."
      />
      <PageHeader
        testId="insurance-header"
        eyebrow="Using Insurance"
      />

      <section data-testid="insurance-section" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={spring}>
          <div className="bg-white rounded-[2rem] border border-border p-8 sm:p-12">
            <div className="flex items-center gap-4">
              <span className="w-14 h-14 rounded-2xl bg-teal-dark flex items-center justify-center shrink-0">
                <ShieldCheck className="w-7 h-7 text-white" strokeWidth={2.25} />
              </span>
              <div>
                <span className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-teal-dark">Insurance at Thrive</span>
                <h2 data-testid="insurance-title" className="font-display text-2xl sm:text-3xl font-semibold text-navy tracking-tight">
                  We accept all major health insurance providers in The Bahamas
                </h2>
              </div>
            </div>

            <div data-testid="insurance-logos" className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {INSURERS.map((insurer) => (
                <div
                  key={insurer.name}
                  data-testid={`insurer-${insurer.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="bg-cream rounded-2xl border border-border h-20 px-4 flex items-center justify-center"
                  title={insurer.name}
                >
                  <img src={insurer.logo} alt={`${insurer.name} logo`} className="max-h-10 max-w-full object-contain" loading="lazy" />
                </div>
              ))}
            </div>

            <h3 className="mt-10 font-display text-xl sm:text-2xl font-semibold text-navy tracking-tight">Using your insurance</h3>
            <p data-testid="insurance-intro" className="mt-3 text-sm sm:text-base text-navy-light leading-relaxed">
              To use health insurance for therapy services at Thrive, a medical referral from your child's pediatrician is required. The referral should indicate the therapy service being requested - such as Physiotherapy, Occupational Therapy, Speech & Language Therapy, or Feeding Therapy. Please provide your insurance information and medical referral before your child's first appointment so our team can verify your benefits.
            </p>

            <div data-testid="insurance-steps" className="mt-8 grid sm:grid-cols-3 gap-4">
              {INSURANCE_STEPS.map((step, i) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ ...spring, delay: i * 0.1 }}
                  data-testid={`insurance-step-${i + 1}`}
                  className="bg-cream rounded-3xl border border-border p-6"
                >
                  <span className="font-display text-3xl font-semibold text-sunny">{step.step}</span>
                  <h4 className="mt-2.5 font-display text-base font-semibold text-navy">{step.title}</h4>
                  <p className="mt-1.5 text-sm text-navy-light leading-relaxed">{step.text}</p>
                </motion.div>
              ))}
            </div>

            <p data-testid="insurance-note" className="mt-6 flex items-start gap-2 text-xs sm:text-sm font-bold text-navy-light/80 leading-relaxed">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-teal-dark" />
              Coverage, copayments, session limits and patient responsibility vary depending on your individual insurance policy.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 bg-teal-soft rounded-3xl px-6 py-5">
              <p className="font-extrabold text-sm sm:text-base text-navy flex-1 text-center sm:text-left">
                Have questions about insurance?
              </p>
              <Link
                to="/contact"
                data-testid="insurance-contact-btn"
                className="inline-flex items-center gap-2 bg-teal-dark text-white font-extrabold text-sm px-6 py-3 rounded-full shadow-lg shadow-teal-dark/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1C565E] focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 shrink-0"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      <CTABanner title="Ready to get started?" />
    </div>
  );
}
